import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { AssetUrls } from 'config/assets';
import { useBlockUserMutation } from 'store/services/block/blockApi';
import './BlockUserModal.css';

interface BlockUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  username: string;
}

const BlockUserModal: React.FC<BlockUserModalProps> = ({
  isOpen,
  onClose,
  userId,
  username,
}) => {
  const [reason, setReason] = useState<string>('');
  const [blockUser, { isLoading }] = useBlockUserMutation();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setReason('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isReasonValid = reason.trim().length >= 10 && reason.trim().length <= 1000;

  const handleSubmit = async () => {
    if (!isReasonValid) return;

    try {
      await blockUser({ userId, username, reason: reason.trim() }).unwrap();
      toast.success(`${username} has been blocked.`);
      onClose();
    } catch (error) {
      console.error('Failed to block user:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const modalContent = (
    <div className="block-user-modal-overlay" onClick={onClose}>
      <div className="block-user-modal" onClick={(e) => e.stopPropagation()}>
        <div className="block-user-modal-header">
          <h2 className="block-user-modal-title">Block {username}?</h2>
          <button className="block-user-close-button" onClick={onClose} aria-label="Close">
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="block-user-modal-content">
          <p className="block-user-modal-body">
            Blocking {username} means you will no longer be matched with them or
            able to start trades with each other. They will not be notified. You
            can unblock them at any time.
          </p>

          <div className="block-user-form-group">
            <label htmlFor="block-reason" className="block-user-form-label">
              Why are you blocking this user? (visible only to TradeMagic staff, required)
            </label>
            <textarea
              id="block-reason"
              className="block-user-form-textarea"
              placeholder="Tell us why you're blocking this user (at least 10 characters)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={1000}
              disabled={isLoading}
            />
            <CharacterCounter
              currentLength={reason.length}
              maxLength={1000}
              warningThreshold={950}
            />
            {reason.length > 0 && reason.trim().length < 10 && (
              <div className="block-user-validation-hint">
                Reason must be at least 10 characters
              </div>
            )}
          </div>
        </div>

        <div className="block-user-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={onClose}
            isDisabled={isLoading}
            fullWidth={false}
          />
          <MagicButton
            label={isLoading ? 'Blocking...' : 'Block user'}
            color="default"
            onPress={handleSubmit}
            isLoading={isLoading}
            isDisabled={!isReasonValid || isLoading}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default BlockUserModal;
